// Сессия пользователя: наши access/refresh JWT (access 30 мин, refresh 30 дней, refresh ОДНОРАЗОВЫЙ).
// - Обновление строго одно за раз (single-flight): параллельные 401 ждут одно и то же обновление.
// - Заранее обновляем, если до истечения < 60 с (живой матч смотрят часами).
// - Новая сессия — только при окончательном отказе сервера (400/401/403 на refresh); сеть/5xx/429 — пробрасываем.
// - Вход: через Telegram (POST account/telegram/, initData проверяет сервер), если ручка уже выкачена;
//   иначе гость (POST account/guest/). Гостевая сессия апгрейдится до Telegram-входа с Bearer гостя — сервер
//   сливает гостевое избранное. Сессия создаётся ЛЕНИВО — при первом действии, которому нужен аккаунт.
// Хранилище — localStorage (webview Telegram его сохраняет; приватный режим — сессия до перезапуска).
import { ApiError, apiRequest, setBearerProvider } from '../api/client';
import { rememberAnalyticsUser } from '../analytics/track';
import { rawInitData, startParam } from '../telegram/webapp';

export type SessionKind = 'guest' | 'telegram';
interface Stored { access: string; refresh: string; expiresAt: number; kind: SessionKind }
interface TokenResponse { access_token: string; refresh_token: string; expires_in?: number; user?: { is_guest?: boolean }; /** обезличенный номер для аналитики (не Telegram id) */ analytics_id?: string }

/** Параметр запуска как есть — сервер запоминает по нему первое касание (рекламную метку); только алфавит Telegram, ≤ 64. */
export const startParamBody = (param: string | undefined): { start_param?: string } =>
  (param && /^[A-Za-z0-9_-]{1,64}$/.test(param) ? { start_param: param } : {});

const KEY = 'cricket.session';
const ACCOUNT = '/api/v5/mobile/account';
let memory: Stored | null = load();
let pending: Promise<Stored> | null = null;
const listeners = new Set<() => void>();

function load(): Stored | null {
  try { return JSON.parse(localStorage.getItem(KEY) ?? 'null') as Stored | null; } catch { return null; }
}
function save(next: Stored | null): void {
  memory = next;
  try { if (next) localStorage.setItem(KEY, JSON.stringify(next)); else localStorage.removeItem(KEY); } catch { /* приватный режим */ }
  listeners.forEach((l) => l());
}
function fromResponse(r: TokenResponse, kind: SessionKind): Stored {
  rememberAnalyticsUser(r.analytics_id);
  return { access: r.access_token, refresh: r.refresh_token, kind,
    expiresAt: Date.now() + (r.expires_in ?? 15 * 60) * 1000 };
}

export function hasSession(): boolean { return memory !== null; }
export function sessionKind(): SessionKind | null { return memory?.kind ?? null; }
export function onSessionChange(listener: () => void): () => void { listeners.add(listener); return () => listeners.delete(listener); }

/** Окончательный отказ: обновлять бессмысленно — нужна новая сессия. */
export function isFinalRejection(error: unknown): boolean {
  return error instanceof ApiError && error.kind === 'http' && [400, 401, 403].includes(error.status);
}

/** Ручки входа через Telegram на сервере ещё может не быть (выкат части B) — тогда гость. */
const notDeployed = (e: unknown) => e instanceof ApiError && (e.status === 404 || e.status === 405);

async function telegramLogin(guestAccess?: string): Promise<Stored | null> {
  const initData = rawInitData();
  if (!initData) return null;
  try {
    const r = await apiRequest<TokenResponse>('POST', `${ACCOUNT}/telegram/`, {
      body: { init_data: initData, ...startParamBody(startParam()) }, headers: guestAccess ? { Authorization: `Bearer ${guestAccess}` } : undefined,
    });
    return fromResponse(r, 'telegram');
  } catch (e) {
    // не выкачена / подпись не сошлась (в т. ч. неподписанный dev-mock без токена бота) / initData просрочен —
    // работаем гостем; сообщение показывать некому: пользователь ничего не просил
    if (notDeployed(e) || (e instanceof ApiError && ['invalid_telegram_init_data', 'telegram_init_data_expired'].includes(e.code ?? ''))) return null;
    throw e;
  }
}

async function createSession(): Promise<Stored> {
  const tg = await telegramLogin();
  if (tg) return tg;
  return fromResponse(await apiRequest<TokenResponse>('POST', `${ACCOUNT}/guest/`, { body: { ...startParamBody(startParam()) } }), 'guest');
}

async function refresh(current: Stored): Promise<Stored> {
  try {
    const r = await apiRequest<TokenResponse>('POST', `${ACCOUNT}/refresh/`, { body: { refresh_token: current.refresh } });
    return fromResponse(r, current.kind);
  } catch (e) {
    if (isFinalRejection(e)) return createSession();
    throw e;
  }
}

/** Single-flight: одно обновление/создание на всё приложение. */
function obtain(work: () => Promise<Stored>): Promise<Stored> {
  if (!pending) {
    pending = work().then((s) => { save(s); return s; }).finally(() => { pending = null; });
  }
  return pending;
}

export async function validToken(): Promise<string> {
  if (pending) return (await pending).access;
  const current = memory;
  if (!current) return (await obtain(createSession)).access;
  if (current.expiresAt - Date.now() < 60_000) return (await obtain(() => refresh(current))).access;
  return current.access;
}

/** Гостя — в Telegram-аккаунт (сервер сливает избранное гостя). Тихо: ошибки не мешают работе гостем. */
export async function upgradeGuestToTelegram(): Promise<void> {
  if (memory?.kind !== 'guest' || !rawInitData()) return;
  try {
    const guest = await validToken();
    const tg = await telegramLogin(guest);
    if (tg) save(tg);
  } catch { /* остаёмся гостем */ }
}

export function signOut(): void { save(null); }

setBearerProvider({
  token: validToken,
  invalidate(rejected) {
    // протух именно этот токен: следующий validToken() обновит (если обновление уже идёт — дождётся его)
    if (memory && memory.access === rejected) memory = { ...memory, expiresAt: 0 };
  },
});
